<template>
    <VJoyWrapper direction="row" justify="flex-start" class="recommendation-authors">
        <VJoyAvatarsList :compress="true">
            <VJoyAvatar
                v-for="recommendation in displayedRecommendations"
                :key="recommendation.accountId"
                size="small"
                :full-name="`${recommendation.authorFirstName} ${recommendation.authorLastName}`"
                :photo-url="recommendation.authorImgUrl"
            />
            <VJoyAvatar v-if="hasMoreRecommendations" size="small" :full-name="`+ ${remainingCount}`" />
        </VJoyAvatarsList>
        <span
            class="recommendation-authors__text"
            v-html="
                t('profile.section.recommendation.authors.count.v2', {
                    recommendationsCount: recommendationsCount,
                    recommenderFullName: firstRecommenderFullName,
                    freelancerFirstName: freelancerFirstName,
                })
            "
        ></span>
    </VJoyWrapper>
</template>

<script lang="ts" setup>
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyAvatar, VJoyAvatarsList, VJoyWrapper} from '@maltjoy/core-vue';
    import {useProfileStore} from '~/store/profileStore';
    import type {Recommendation} from '~/types/domain/Recommendations';

    // Props
    const props = defineProps<{
        recommendations: Recommendation[];
    }>();

    // Stores
    const {profile} = storeToRefs(useProfileStore());

    // Composables
    const {t} = useTranslation();

    // Constants
    const AVATAR_DISPLAY_LIMIT = 3;

    // Computed
    const recommendationsCount = computed(() => props.recommendations.length ?? 0);
    const freelancerFirstName = computed(() => profile.value?.firstname ?? '');
    const displayedRecommendations = computed(() => props.recommendations.slice(0, AVATAR_DISPLAY_LIMIT));
    const hasMoreRecommendations = computed(() => props.recommendations.length > AVATAR_DISPLAY_LIMIT);
    const remainingCount = computed(() => props.recommendations.length - AVATAR_DISPLAY_LIMIT);
    const firstRecommenderFullName = computed(() => {
        const first = props.recommendations[0];
        return first ? `${first.authorFirstName} ${first.authorLastName}` : '';
    });
</script>

<style lang="scss" scoped>
    .recommendation-authors {
        &__text {
            flex: 1;
        }
    }
</style>
